import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from "sonner";
import LogoDark from '../assets/logo-dark.svg?url'
import LogoLight from '../assets/logo-light.svg?url'
import { useTheme } from '@/components/ThemeProvider'

const subscribeSchema = z.object({
  email: z.string().email()
})

type SubscribeForm = z.infer<typeof subscribeSchema>

export function ComingSoon() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { theme } = useTheme()
  
  const { register, handleSubmit, formState: { errors } } = useForm<SubscribeForm>({
    resolver: zodResolver(subscribeSchema)
  })

  const onSubmit = async (data: SubscribeForm) => {
    setIsSubmitting(true)
    try {
      // TODO: Implement subscription logic
      toast.success("Email submitted successfully");
    } catch (error) {
      toast.error("Failed to submit email");
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background-primary to-secondary/10 dark:from-primary-dark/10 dark:via-background-dark-primary dark:to-secondary-dark/10 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg text-center space-y-8">
        {/* Logo */}
        <div className="animate-fade-in">
          <img 
            src={theme === 'dark' ? LogoDark : LogoLight}
            alt="Flows.Cash" 
            className="w-64 h-64 mx-auto mb-8 animate-float"
          />
          <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            Flows.Cash
          </h1>
          <p className="mt-2 text-2xl font-semibold text-text-secondary dark:text-text-dark-secondary">
            Your Money, Your Flow
          </p>
          <p className="mt-4 text-lg text-text-secondary dark:text-text-dark-secondary">
            A revolutionary personal finance management platform that helps you track expenses, manage accounts, and achieve your financial goals with ease.
          </p>
        </div>

        {/* Subscription Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Input
              type="email"
              placeholder="Enter your email"
              {...register('email')}
              className={errors.email ? 'border-red-500 dark:border-red-400' : 'border-border dark:border-border-dark'}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500 dark:text-red-400">{errors.email.message}</p>
            )}
          </div>
          <Button 
            type="submit" 
            className="w-full bg-primary text-white hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Subscribing..." : "Notify me when it launches"}
          </Button>
        </form>

        {/* Social Links */}
        <div className="flex justify-center space-x-4">
          <a href="https://twitter.com/flowscash" target="_blank" rel="noopener noreferrer" className="text-text-secondary dark:text-text-dark-secondary hover:text-secondary dark:hover:text-primary-dark transition-colors">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
            </svg>
          </a>
          <a href="https://github.com/flowscash" target="_blank" rel="noopener noreferrer" className="text-text-secondary dark:text-text-dark-secondary hover:text-secondary dark:hover:text-primary-dark transition-colors">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>

      {/* Add floating animation keyframes */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
      `}</style>
    </div>
  )
}
